import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useCompany } from '../context/CompanyContext';
import { format } from 'date-fns';
import {
  Plus, Search, Loader2, X, FileText, Trash2, Copy,
  MoreHorizontal, Pencil, Upload, Filter, RefreshCw, Pause, Play, Clock, CreditCard
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../components/ui/popover';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { ExportImportButtons } from '../components/ExportImportButtons';
import { useExportImport } from '../hooks/useExportImport';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAYMENT_METHODS = ['Cash', 'Credit Card', 'Debit Card', 'Bank Transfer', 'Check', 'PayPal', 'Other'];
const FREQUENCIES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
];

const fmtCurrency = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(v || 0);

// ── Combobox-style input: pick from list OR type new ──
function ComboInput({ value, onChange, options, placeholder, testId, colorMap }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => { setQuery(value || ''); }, [value]);

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, []);

  const filtered = options.filter(o => o.toLowerCase().includes(query.toLowerCase()));
  const isNew = query.trim() && !options.some(o => o.toLowerCase() === query.trim().toLowerCase());

  return (
    <div ref={wrapperRef} className="relative">
      <Input
        ref={inputRef}
        value={query}
        onChange={e => { setQuery(e.target.value); onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full"
        autoComplete="off"
      />
      {open && (filtered.length > 0 || isNew) && (
        <div className="absolute z-[100] mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {filtered.map((opt, i) => (
            <button
              key={i}
              type="button"
              onPointerDown={(e) => { e.preventDefault(); onChange(opt); setQuery(opt); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2"
            >
              {colorMap?.[opt] && <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: colorMap[opt] }} />}
              {opt}
            </button>
          ))}
          {isNew && (
            <button
              type="button"
              onPointerDown={(e) => { e.preventDefault(); onChange(query.trim()); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 text-blue-600 font-medium border-t border-gray-100 flex items-center gap-2"
              data-testid={`${testId}-create-new`}
            >
              <Plus className="w-3.5 h-3.5" />
              Create "{query.trim()}"
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Add/Edit Expense Dialog ──
function ExpenseDialog({ open, onOpenChange, expense, categories, companyId, onSave, onCategoriesRefresh }) {
  const [form, setForm] = useState({
    description: '', amount: '', date: new Date().toISOString().slice(0, 10),
    category: '', subcategory: '', vendor: '', payment_method: '',
    country: '', notes: '', file_id: '', is_recurring: false, recurring_frequency: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const receiptRef = useRef(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);

  useEffect(() => {
    if (open) {
      if (expense) {
        setForm({
          description: expense.description || '', amount: String(expense.amount || ''),
          date: expense.date || new Date().toISOString().slice(0, 10),
          category: expense.category || '', subcategory: expense.subcategory || '',
          vendor: expense.vendor || '', payment_method: expense.payment_method || '',
          country: expense.country || '', notes: expense.notes || '',
          file_id: expense.file_id || '',
          is_recurring: expense.is_recurring || false,
          recurring_frequency: expense.recurring_frequency || ''
        });
      } else {
        setForm({
          description: '', amount: '', date: new Date().toISOString().slice(0, 10),
          category: '', subcategory: '', vendor: '', payment_method: '',
          country: '', notes: '', file_id: '', is_recurring: false, recurring_frequency: ''
        });
      }
    }
  }, [expense, open]);

  const catNames = categories.map(c => c.name);
  const catColors = {};
  categories.forEach(c => { catColors[c.name] = c.color; });
  const currentCat = categories.find(c => c.name === form.category);
  const subcatOptions = currentCat?.subcategories || [];

  const ensureCategoryExists = async (catName) => {
    if (!catName || catNames.some(n => n.toLowerCase() === catName.toLowerCase())) return;
    try {
      await axios.post(`${API_URL}/api/categories`, {
        company_id: companyId, name: catName, color: '#6B7280', subcategories: []
      }, { withCredentials: true });
      onCategoriesRefresh?.();
    } catch { /* ignore if already exists */ }
  };

  const ensureSubcategoryExists = async (catName, subName) => {
    if (!catName || !subName) return;
    const cat = categories.find(c => c.name.toLowerCase() === catName.toLowerCase());
    if (!cat) return;
    if ((cat.subcategories || []).some(s => s.toLowerCase() === subName.toLowerCase())) return;
    try {
      await axios.put(`${API_URL}/api/categories/${cat.id}`, {
        subcategories: [...(cat.subcategories || []), subName]
      }, { withCredentials: true });
      onCategoriesRefresh?.();
    } catch { /* ignore */ }
  };

  const handleReceiptUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingReceipt(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/api/files/upload?company_id=${companyId}&tag=receipt`, fd, { withCredentials: true });
      setForm(p => ({ ...p, file_id: res.data.id }));
      toast.success('Receipt attached');
    } catch { toast.error('Upload failed'); }
    finally { setUploadingReceipt(false); e.target.value = ''; }
  };

  const handleSubmit = async () => {
    if (!form.description.trim() || !form.amount) { toast.error('Description and amount required'); return; }
    setSubmitting(true);
    try {
      // Auto-create category / subcategory if new
      await ensureCategoryExists(form.category);
      if (form.subcategory) await ensureSubcategoryExists(form.category, form.subcategory);

      const payload = {
        ...form,
        amount: parseFloat(form.amount),
        company_id: companyId,
        file_id: form.file_id || null,
        subcategory: form.subcategory || null,
        vendor: form.vendor || null,
        payment_method: form.payment_method || null,
        country: form.country || null,
        is_recurring: form.is_recurring,
        recurring_frequency: form.is_recurring ? form.recurring_frequency : null,
      };
      if (expense) {
        await axios.put(`${API_URL}/api/expenses/${expense.id}`, payload, { withCredentials: true });
        toast.success('Expense updated');
      } else {
        await axios.post(`${API_URL}/api/expenses`, payload, { withCredentials: true });
        toast.success('Expense added');
      }
      onSave(); onOpenChange(false);
    } catch { toast.error('Failed to save'); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{expense ? 'Edit Expense' : 'Add Expense'}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label>Description / Payee *</Label>
            <Input value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} placeholder="What was this expense for?" data-testid="expense-description-input" />
          </div>
          <div>
            <Label>Amount *</Label>
            <Input type="number" step="0.01" value={form.amount} onChange={e => setForm(p => ({...p, amount: e.target.value}))} placeholder="0.00" data-testid="expense-amount-input" />
          </div>
          <div>
            <Label>Date</Label>
            <Input type="date" value={form.date} onChange={e => setForm(p => ({...p, date: e.target.value}))} data-testid="expense-date-input" />
          </div>
          <div>
            <Label>Category</Label>
            <ComboInput
              value={form.category}
              onChange={v => setForm(p => ({...p, category: v, subcategory: ''}))}
              options={catNames}
              placeholder="Select or type new..."
              testId="expense-category-input"
              colorMap={catColors}
            />
          </div>
          <div>
            <Label>Subcategory</Label>
            <ComboInput
              value={form.subcategory}
              onChange={v => setForm(p => ({...p, subcategory: v}))}
              options={subcatOptions}
              placeholder="Select or type new..."
              testId="expense-subcategory-input"
            />
          </div>
          <div>
            <Label>Vendor</Label>
            <Input value={form.vendor} onChange={e => setForm(p => ({...p, vendor: e.target.value}))} placeholder="Vendor name" data-testid="expense-vendor-input" />
          </div>
          <div>
            <Label>Payment Method</Label>
            <Select value={form.payment_method} onValueChange={v => setForm(p => ({...p, payment_method: v}))}>
              <SelectTrigger data-testid="expense-payment-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {PAYMENT_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Country</Label>
            <Input value={form.country} onChange={e => setForm(p => ({...p, country: e.target.value}))} placeholder="Country" data-testid="expense-country-input" />
          </div>
          <div className="col-span-2">
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} rows={2} placeholder="Additional notes..." data-testid="expense-notes-input" />
          </div>
          <div className="col-span-2">
            <Label>Receipt</Label>
            <div className="flex items-center gap-2 mt-1">
              <Button variant="outline" size="sm" onClick={() => receiptRef.current?.click()} disabled={uploadingReceipt}>
                {uploadingReceipt ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                {form.file_id ? 'Replace Receipt' : 'Attach Receipt'}
              </Button>
              {form.file_id && <span className="text-xs text-blue-600">Receipt attached</span>}
              <input ref={receiptRef} type="file" className="hidden" accept="image/*,.pdf" onChange={handleReceiptUpload} />
            </div>
          </div>
          {/* Recurring */}
          <div className="col-span-2 border-t border-gray-100 pt-3 mt-1">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <RefreshCw className="w-4 h-4 text-gray-400" />
                <Label className="mb-0">Recurring Expense</Label>
              </div>
              <Switch
                checked={form.is_recurring}
                onCheckedChange={v => setForm(p => ({ ...p, is_recurring: v, recurring_frequency: v ? (p.recurring_frequency || 'monthly') : '' }))}
                data-testid="recurring-toggle"
              />
            </div>
            {form.is_recurring && (
              <div className="mt-2">
                <Label className="text-xs text-gray-500">Frequency</Label>
                <Select value={form.recurring_frequency} onValueChange={v => setForm(p => ({ ...p, recurring_frequency: v }))}>
                  <SelectTrigger data-testid="recurring-frequency-select"><SelectValue placeholder="Select frequency" /></SelectTrigger>
                  <SelectContent>
                    {FREQUENCIES.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="save-expense-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : expense ? 'Update' : 'Add Expense'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main ExpensesPage ──
export default function ExpensesPage() {
  const { currentCompany } = useCompany();
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editExpense, setEditExpense] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [recurringTemplates, setRecurringTemplates] = useState([]);
  const [showRecurring, setShowRecurring] = useState(false);
  const [deleteRecurringTarget, setDeleteRecurringTarget] = useState(null);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [brexSyncing, setBrexSyncing] = useState(false);
  const [brexStatus, setBrexStatus] = useState(null);

  // Filters
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [subFilter, setSubFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [sortField, setSortField] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [selected, setSelected] = useState([]);

  const { handleExportCSV, handleImportCSV, handleImportPDF, exporting, importing, importingPDF } = useExportImport({
    exportUrl: '/api/expenses/export/csv',
    importUrl: '/api/expenses/import/csv',
    importPdfUrl: '/api/expenses/import/pdf',
    companyId: currentCompany?.id,
    onImportSuccess: () => fetchExpenses(),
    entityName: 'expenses'
  });

  const fetchExpenses = useCallback(async () => {
    if (!currentCompany) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/expenses`, { params: { company_id: currentCompany.id }, withCredentials: true });
      setExpenses(res.data);
    } catch { toast.error('Failed to load expenses'); }
    finally { setLoading(false); }
  }, [currentCompany]);

  const fetchCategories = useCallback(async () => {
    if (!currentCompany) return;
    try {
      const res = await axios.get(`${API_URL}/api/categories`, { params: { company_id: currentCompany.id }, withCredentials: true });
      setCategories(res.data);
    } catch { /* ignore */ }
  }, [currentCompany]);

  const fetchRecurring = useCallback(async () => {
    if (!currentCompany) return;
    try {
      const res = await axios.get(`${API_URL}/api/recurring-templates`, { params: { company_id: currentCompany.id }, withCredentials: true });
      setRecurringTemplates(res.data);
    } catch { /* ignore */ }
  }, [currentCompany]);

  // Auto-generate due recurring expenses on page load
  const generateRecurring = useCallback(async () => {
    if (!currentCompany) return;
    try {
      const res = await axios.post(`${API_URL}/api/recurring-templates/generate`, null, {
        params: { company_id: currentCompany.id }, withCredentials: true
      });
      if (res.data.generated > 0) {
        toast.success(`${res.data.generated} recurring expense(s) generated`);
        fetchExpenses();
      }
    } catch { /* ignore */ }
  }, [currentCompany, fetchExpenses]);

  useEffect(() => { fetchExpenses(); fetchCategories(); fetchRecurring(); }, [fetchExpenses, fetchCategories, fetchRecurring]);
  useEffect(() => { generateRecurring(); }, [generateRecurring]);

  // Brex sync
  const fetchBrexStatus = useCallback(async () => {
    if (!currentCompany) return;
    try {
      const res = await axios.get(`${API_URL}/api/brex/status`, { params: { company_id: currentCompany.id }, withCredentials: true });
      setBrexStatus(res.data);
    } catch { /* ignore */ }
  }, [currentCompany]);

  useEffect(() => { fetchBrexStatus(); }, [fetchBrexStatus]);

  // Auto-sync Brex on page load (once) — Veriquant only
  const brexSyncDone = useRef(false);
  useEffect(() => {
    if (!currentCompany || currentCompany.slug !== 'veriquant' || !brexStatus?.configured || brexSyncDone.current) return;
    brexSyncDone.current = true;
    const autoSync = async () => {
      try {
        const res = await axios.post(`${API_URL}/api/brex/sync`, { company_id: currentCompany.id }, { withCredentials: true });
        if (res.data.imported > 0) {
          toast.success(`${res.data.imported} new Brex transaction${res.data.imported > 1 ? 's' : ''} synced`);
          fetchExpenses();
          fetchBrexStatus();
        }
      } catch { /* silent */ }
    };
    autoSync();
  }, [currentCompany, brexStatus?.configured]);

  const handleBrexSync = async () => {
    setBrexSyncing(true);
    try {
      const res = await axios.post(`${API_URL}/api/brex/sync`, { company_id: currentCompany.id }, { withCredentials: true });
      const msgs = [];
      if (res.data.imported > 0) msgs.push(`${res.data.imported} expenses`);
      if (res.data.revenue_imported > 0) msgs.push(`${res.data.revenue_imported} deposits`);
      if (msgs.length > 0) {
        toast.success(`Imported ${msgs.join(' + ')} from Brex`);
      } else if ((res.data.skipped || 0) + (res.data.revenue_skipped || 0) > 0) {
        toast.info('Already up to date');
      } else {
        toast.info('No new transactions found');
      }
      fetchExpenses();
      fetchBrexStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Brex sync failed');
    }
    finally { setBrexSyncing(false); }
  };

  const catColors = {};
  categories.forEach(c => { catColors[c.name] = c.color; });

  const filteredSubcats = catFilter !== 'all' ? (categories.find(c => c.name === catFilter)?.subcategories || []) : [];

  const filtered = expenses.filter(e => {
    if (catFilter !== 'all' && e.category !== catFilter) return false;
    if (subFilter !== 'all' && e.subcategory !== subFilter) return false;
    if (paymentFilter !== 'all' && e.payment_method !== paymentFilter) return false;
    if (startDate && e.date < format(startDate, 'yyyy-MM-dd')) return false;
    if (endDate && e.date > format(endDate, 'yyyy-MM-dd')) return false;
    if (search) {
      const q = search.toLowerCase();
      return (e.description || '').toLowerCase().includes(q) ||
        (e.vendor || '').toLowerCase().includes(q) ||
        (e.notes || '').toLowerCase().includes(q) ||
        (e.category || '').toLowerCase().includes(q) ||
        (e.subcategory || '').toLowerCase().includes(q) ||
        (e.payment_method || '').toLowerCase().includes(q);
    }
    return true;
  }).sort((a, b) => {
    let va = a[sortField], vb = b[sortField];
    if (sortField === 'amount') { va = Number(va) || 0; vb = Number(vb) || 0; }
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const toggleSelect = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const toggleAll = () => setSelected(s => s.length === filtered.length ? [] : filtered.map(e => e.id));

  const handleDuplicate = async (id) => {
    try {
      await axios.post(`${API_URL}/api/expenses/${id}/duplicate`, {}, { withCredentials: true });
      toast.success('Expense duplicated'); fetchExpenses();
    } catch { toast.error('Failed to duplicate'); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/api/expenses/${deleteTarget.id}`, { withCredentials: true });
      toast.success('Expense deleted'); setDeleteTarget(null); fetchExpenses();
    } catch { toast.error('Delete failed'); }
  };

  const handleBulkDelete = async () => {
    try {
      await axios.post(`${API_URL}/api/expenses/bulk-delete`, { ids: selected }, { withCredentials: true });
      toast.success(`Deleted ${selected.length} expense(s)`);
      setSelected([]); fetchExpenses();
    } catch { toast.error('Bulk delete failed'); }
  };

  const clearFilters = () => {
    setCatFilter('all'); setSubFilter('all'); setPaymentFilter('all');
    setStartDate(null); setEndDate(null); setSearch('');
  };

  const handleToggleRecurring = async (tmpl) => {
    try {
      await axios.put(`${API_URL}/api/recurring-templates/${tmpl.id}`, { is_active: !tmpl.is_active }, { withCredentials: true });
      toast.success(tmpl.is_active ? 'Recurring paused' : 'Recurring resumed');
      fetchRecurring();
    } catch { toast.error('Failed to update'); }
  };

  const handleDeleteRecurring = async () => {
    try {
      await axios.delete(`${API_URL}/api/recurring-templates/${deleteRecurringTarget.id}`, { withCredentials: true });
      toast.success('Recurring template deleted');
      setDeleteRecurringTarget(null);
      fetchRecurring();
    } catch { toast.error('Delete failed'); }
  };

  const hasFilters = catFilter !== 'all' || subFilter !== 'all' || paymentFilter !== 'all' || startDate || endDate || search;

  const SortHeader = ({ field, children }) => (
    <TableHead onClick={() => toggleSort(field)} className="cursor-pointer hover:text-gray-900 select-none text-xs uppercase tracking-wider">
      <div className="flex items-center gap-1">
        {children}
        {sortField === field && <span className="text-[10px]">{sortDir === 'asc' ? '▲' : '▼'}</span>}
      </div>
    </TableHead>
  );

  if (!currentCompany) return <div className="flex items-center justify-center h-64"><p className="text-gray-500">Please select a company</p></div>;

  return (
    <div className="space-y-5" data-testid="expenses-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Expenses</h1>
          <p className="text-gray-500 mt-1">{currentCompany.name}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ExportImportButtons onExportCSV={handleExportCSV} onImportCSV={handleImportCSV} onImportPDF={handleImportPDF} exporting={exporting} importing={importing} importingPDF={importingPDF} testIdPrefix="expenses" />
          {brexStatus?.configured && currentCompany?.slug === 'veriquant' && (
            <Button variant="outline" size="sm" onClick={handleBrexSync} disabled={brexSyncing} data-testid="brex-sync-btn" className="border-orange-200 text-orange-700 hover:bg-orange-50">
              {brexSyncing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CreditCard className="w-4 h-4 mr-1.5" />}
              {brexSyncing ? 'Syncing...' : 'Sync Brex'}
              {brexStatus?.total_brex_expenses > 0 && <span className="ml-1 text-xs text-orange-500">({brexStatus.total_brex_expenses})</span>}
            </Button>
          )}
          {recurringTemplates.length > 0 && (
            <Button variant="outline" size="sm" onClick={() => setShowRecurring(v => !v)} data-testid="manage-recurring-btn" className={showRecurring ? 'border-indigo-300 text-indigo-600' : ''}>
              <RefreshCw className="w-4 h-4 mr-1.5" />Recurring ({recurringTemplates.filter(t => t.is_active).length})
            </Button>
          )}
          <Button onClick={() => setAddOpen(true)} data-testid="add-expense-btn">
            <Plus className="w-4 h-4 mr-2" />Add Expense
          </Button>
        </div>
      </div>

      {/* Recurring Templates Panel */}
      {showRecurring && recurringTemplates.length > 0 && (
        <div className="bg-indigo-50/50 border border-indigo-100 rounded-xl p-4" data-testid="recurring-panel">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-indigo-900 flex items-center gap-1.5">
              <RefreshCw className="w-4 h-4" />
              Recurring Expenses
            </h3>
            <Button variant="ghost" size="sm" onClick={() => setShowRecurring(false)}>
              <X className="w-4 h-4" />
            </Button>
          </div>
          <div className="space-y-2">
            {recurringTemplates.map(tmpl => (
              <div key={tmpl.id} className={`flex items-center justify-between bg-white rounded-lg px-4 py-2.5 border ${tmpl.is_active ? 'border-indigo-200' : 'border-gray-200 opacity-60'}`} data-testid={`recurring-tmpl-${tmpl.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900 truncate">{tmpl.description}</span>
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-indigo-100 text-indigo-700">{tmpl.frequency}</span>
                    {!tmpl.is_active && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-gray-100 text-gray-500">Paused</span>}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
                    <span>{fmtCurrency(tmpl.amount)}</span>
                    <span>{tmpl.category}</span>
                    {tmpl.next_due_date && <span>Next: {tmpl.next_due_date}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1 ml-3">
                  <button onClick={() => handleToggleRecurring(tmpl)} className="p-1.5 rounded hover:bg-gray-100" title={tmpl.is_active ? 'Pause' : 'Resume'}>
                    {tmpl.is_active ? <Pause className="w-3.5 h-3.5 text-amber-500" /> : <Play className="w-3.5 h-3.5 text-blue-500" />}
                  </button>
                  <button onClick={() => setDeleteRecurringTarget(tmpl)} className="p-1.5 rounded hover:bg-red-50">
                    <Trash2 className="w-3.5 h-3.5 text-red-400" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Category Filter Tabs */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1" data-testid="category-filter-tabs">
        <button
          onClick={() => { setCatFilter('all'); setSubFilter('all'); }}
          className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${catFilter === 'all' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          data-testid="cat-filter-all"
        >
          All ({expenses.length})
        </button>
        {categories.map(cat => {
          const count = expenses.filter(e => e.category === cat.name).length;
          return (
            <button
              key={cat.id}
              onClick={() => { setCatFilter(cat.name); setSubFilter('all'); }}
              className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-1.5 ${catFilter === cat.name ? 'text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              style={catFilter === cat.name ? { backgroundColor: cat.color } : {}}
              data-testid={`cat-filter-${cat.name.toLowerCase()}`}
            >
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: catFilter === cat.name ? '#fff' : cat.color }} />
              {cat.name} ({count})
            </button>
          );
        })}
      </div>

      {/* Subcategory filter (show only when a category is selected) */}
      {catFilter !== 'all' && filteredSubcats.length > 0 && (
        <div className="flex items-center gap-2 overflow-x-auto" data-testid="subcategory-filter-tabs">
          <span className="text-xs text-gray-400 mr-1">Sub:</span>
          <button
            onClick={() => setSubFilter('all')}
            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${subFilter === 'all' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
          >All</button>
          {filteredSubcats.map((sub, i) => (
            <button
              key={i}
              onClick={() => setSubFilter(sub)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${subFilter === sub ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
            >{sub}</button>
          ))}
        </div>
      )}

      {/* Search & Filters Row */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search expenses..." className="pl-10" data-testid="expense-search" />
        </div>
        <Select value={paymentFilter} onValueChange={setPaymentFilter}>
          <SelectTrigger className="w-40" data-testid="payment-filter"><SelectValue placeholder="Payment" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Methods</SelectItem>
            {PAYMENT_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="text-sm" data-testid="date-from-filter">
              {startDate ? format(startDate, 'MM/dd/yy') : 'From'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={startDate} onSelect={setStartDate} /></PopoverContent>
        </Popover>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="text-sm" data-testid="date-to-filter">
              {endDate ? format(endDate, 'MM/dd/yy') : 'To'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={endDate} onSelect={setEndDate} /></PopoverContent>
        </Popover>
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="text-gray-400 hover:text-gray-600" data-testid="clear-filters-btn">
            <X className="w-4 h-4 mr-1" />Clear
          </Button>
        )}
      </div>

      {/* Bulk Actions */}
      {selected.length > 0 && (
        <div className="flex items-center justify-between bg-indigo-50 rounded-lg px-4 py-2.5 border border-indigo-200" data-testid="bulk-actions">
          <span className="text-sm text-indigo-900 font-medium">{selected.length} expense{selected.length !== 1 ? 's' : ''} selected</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setBulkDeleteConfirm(true)} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="bulk-delete-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" />Delete Selected
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setSelected([])} data-testid="clear-selection-btn">Clear</Button>
          </div>
        </div>
      )}

      {/* Expenses Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="w-10">
                <Checkbox checked={selected.length === filtered.length && filtered.length > 0} onCheckedChange={toggleAll} />
              </TableHead>
              <SortHeader field="date">Date</SortHeader>
              <SortHeader field="description">Description</SortHeader>
              <SortHeader field="category">Category</SortHeader>
              <TableHead className="text-xs uppercase tracking-wider">Vendor</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Payment</TableHead>
              <SortHeader field="amount">Amount</SortHeader>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" /></TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-16">
                  <div className="text-gray-400">
                    <p className="text-base font-medium mb-1">No expenses found</p>
                    <p className="text-sm">{hasFilters ? 'Try adjusting your filters' : 'Click "Add Expense" to get started'}</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : filtered.map((e) => (
              <TableRow key={e.id} className="hover:bg-gray-50" data-testid={`expense-row-${e.id}`}>
                <TableCell><Checkbox checked={selected.includes(e.id)} onCheckedChange={() => toggleSelect(e.id)} /></TableCell>
                <TableCell className="text-sm text-gray-600 whitespace-nowrap">{e.date}</TableCell>
                <TableCell>
                  <div className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                    {e.description}
                    {e.is_recurring && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600" title={`Recurring ${e.recurring_frequency || ''}`}>
                        <RefreshCw className="w-3 h-3" />
                        <span className="text-[10px] font-semibold uppercase">{e.recurring_frequency?.[0]}</span>
                      </span>
                    )}
                  </div>
                  {e.notes && <p className="text-xs text-gray-400 truncate max-w-[200px]">{e.notes}</p>}
                </TableCell>
                <TableCell>
                  <div className="flex flex-col gap-0.5">
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium w-fit" style={{ backgroundColor: (catColors[e.category] || '#6B7280') + '15', color: catColors[e.category] || '#6B7280' }}>
                      <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: catColors[e.category] || '#6B7280' }} />
                      {e.category || 'Uncategorized'}
                    </span>
                    {e.subcategory && <span className="text-[11px] text-gray-400 pl-1">{e.subcategory}</span>}
                  </div>
                </TableCell>
                <TableCell className="text-sm text-gray-600">{e.vendor || '-'}</TableCell>
                <TableCell className="text-sm text-gray-600">{e.payment_method || '-'}</TableCell>
                <TableCell className="text-sm font-semibold text-gray-900 text-right">{fmtCurrency(e.amount)}</TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button variant="ghost" size="sm" className="h-7 w-7 p-0"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setEditExpense(e)}><Pencil className="w-3.5 h-3.5 mr-2" />Edit</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDuplicate(e.id)}><Copy className="w-3.5 h-3.5 mr-2" />Duplicate</DropdownMenuItem>
                      {e.file_id && <DropdownMenuItem onClick={() => window.open(`${API_URL}/api/files/${e.file_id}/download`, '_blank')}><FileText className="w-3.5 h-3.5 mr-2" />View Receipt</DropdownMenuItem>}
                      <DropdownMenuItem onClick={() => setDeleteTarget(e)} className="text-red-600"><Trash2 className="w-3.5 h-3.5 mr-2" />Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Count */}
      <div className="flex justify-end px-2">
        <span className="text-sm text-gray-500">
          {filtered.length} expense{filtered.length !== 1 ? 's' : ''}
          {hasFilters && ` (of ${expenses.length})`}
        </span>
      </div>

      {/* Add/Edit Dialog */}
      <ExpenseDialog
        open={addOpen || !!editExpense}
        onOpenChange={(v) => { if (!v) { setAddOpen(false); setEditExpense(null); } }}
        expense={editExpense}
        categories={categories}
        companyId={currentCompany.id}
        onSave={() => { fetchExpenses(); fetchCategories(); fetchRecurring(); }}
        onCategoriesRefresh={fetchCategories}
      />

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Expense</AlertDialogTitle>
            <AlertDialogDescription>Delete "{deleteTarget?.description}" ({fmtCurrency(deleteTarget?.amount)})?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete recurring template confirm */}
      <AlertDialog open={!!deleteRecurringTarget} onOpenChange={() => setDeleteRecurringTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Recurring Template</AlertDialogTitle>
            <AlertDialogDescription>
              Stop recurring "{deleteRecurringTarget?.description}" ({deleteRecurringTarget?.frequency})? Existing generated expenses will not be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteRecurring} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk delete confirm */}
      <AlertDialog open={bulkDeleteConfirm} onOpenChange={setBulkDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selected.length} Expense{selected.length !== 1 ? 's' : ''}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {selected.length} selected expense{selected.length !== 1 ? 's' : ''}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setBulkDeleteConfirm(false); handleBulkDelete(); }} className="bg-red-600 hover:bg-red-700" data-testid="confirm-bulk-delete-btn">Delete All</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
